import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Code,
  Visibility,
  Close,
} from '@mui/icons-material';
import { THEME_COLORS, OPERATION_STATUS, UX_TIMING } from '../../constants';
import { useDistributorTemplates } from '../../hooks/useDistributorTemplates';
import ConfirmationModal from '../forms/ConfirmationModal';
import DistributorTemplateVariablesPicker from '../forms/DistributorTemplateVariablesPicker';
import distributorTemplateVariableService from '../../services/distributorTemplateVariableService';

const templateMetadata = {
  distributor_created: {
    label: 'Distribuidor Creado',
    helperText: 'Se envía cuando se registra un nuevo distribuidor en el sistema.',
  },
  distributor_activated: {
    label: 'Distribuidor Activado',
    helperText: 'Se envía cuando se activa la cuenta de un distribuidor.',
  },
  distributor_deactivated: {
    label: 'Distribuidor Desactivado',
    helperText: 'Se envía cuando se desactiva temporalmente un distribuidor.',
  },
  distributor_updated: {
    label: 'Distribuidor Actualizado',
    helperText: 'Se envía cuando se actualizan los datos de un distribuidor.',
  },
};

// Componente de información sobre el comportamiento de las plantillas
const TemplateInfo = () => (
  <Paper variant="outlined" sx={{ 
    p: 3, 
    backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
    borderRadius: '12px',
    border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
    mb: 2 
  }}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <InfoIcon sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 0.5 }} />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, mb: 1 }}>
          💡 Comportamiento de las Plantillas de Distribuidores
        </Typography>
        <Typography variant="caption" sx={{ color: THEME_COLORS.TEXT_SECONDARY, lineHeight: 1.5 }}>
          • Las plantillas se activan automáticamente según los eventos de distribuidores<br/>
          • Puedes desactivar plantillas específicas (el texto se conserva)<br/>
          • Solo las plantillas activas enviarán notificaciones automáticas<br/>
          • Las variables se reemplazan automáticamente con datos reales del distribuidor
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const DistributorTemplates = () => {
  const {
    templates,
    loading,
    error,
    loadTemplates,
    updateAllTemplates,
    operationStatus,
    operatingTemplateName,
    resetOperationState,
  } = useDistributorTemplates();

  const [editableTemplates, setEditableTemplates] = useState([]);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  // Referencias a los textareas para insertar variables
  const textareaRefs = useRef({});

  useEffect(() => {
    console.log('DistributorTemplates: Cargando plantillas...');
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    console.log('DistributorTemplates: Templates recibidos:', templates);
    if (templates.length > 0) {
      setEditableTemplates(templates);
    }
  }, [templates]);

  console.log('DistributorTemplates: Estado actual - loading:', loading, 'error:', error, 'templates:', templates);

  const handleTemplateChange = (templateId, field, value) => {
    setEditableTemplates(prev =>
      prev.map(t =>
        t.template_id === templateId ? { ...t, [field]: value } : t
      )
    );
  };

  const handleSave = async () => {
    setShowOperationModal(true);
    const result = await updateAllTemplates(editableTemplates);
    
    const delay = result.success ? UX_TIMING.SUCCESS_DISPLAY_TIME : UX_TIMING.ERROR_DISPLAY_TIME;
    
    setTimeout(() => {
      setShowOperationModal(false);
      resetOperationState();
    }, delay);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleVariableSelect = (variable) => {
    // Buscar el textarea actualmente enfocado
    const focusedElement = document.activeElement;
    
    if (focusedElement && focusedElement.tagName === 'TEXTAREA') {
      // Si hay un textarea enfocado, insertar ahí
      distributorTemplateVariableService.insertVariableInTextarea(variable, focusedElement);
    } else {
      // Si no hay focus específico, insertar en el primer textarea disponible
      const firstTextarea = Object.values(textareaRefs.current)[0];
      if (firstTextarea) {
        distributorTemplateVariableService.insertVariableInTextarea(variable, firstTextarea);
      }
    }
  };
  
  const renderTemplateCards = () => {
    if (loading && templates.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
      <Grid container spacing={3}>
        {editableTemplates.map(template => (
          <Grid item xs={12} key={template.template_id}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`, 
                borderRadius: '12px'
              }}
            >
              <Typography variant="subtitle1" gutterBottom sx={{ 
                fontWeight: 600, 
                color: THEME_COLORS.TEXT_PRIMARY,
                fontSize: '1.1rem',
                mb: 2
              }}>
                {templateMetadata[template.event_type]?.label || template.event_type}
              </Typography>
              
              <TextField
                inputRef={el => textareaRefs.current[template.template_id] = el}
                fullWidth
                multiline
                rows={6}
                label="Mensaje de la plantilla"
                value={template.message_body}
                onChange={(e) => handleTemplateChange(template.template_id, 'message_body', e.target.value)}
                placeholder="Ej: ¡Bienvenido {{distribuidor_nombre}}! Tu registro como distribuidor ha sido exitoso..."
                helperText={templateMetadata[template.event_type]?.helperText || ''}
                variant="outlined"
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.9rem'
                    }
                  }
                }}
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={!!template.is_active}
                    onChange={(e) => handleTemplateChange(template.template_id, 'is_active', e.target.checked)}
                  />
                }
                label={
                  template.is_active 
                    ? "Plantilla activa" 
                    : "Plantilla inactiva"
                }
                sx={{ 
                  color: template.is_active 
                    ? THEME_COLORS.TEXT_PRIMARY
                    : THEME_COLORS.TEXT_SECONDARY
                }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />}
            sx={{
              backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
              borderBottom: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              py: 2,
              '& .MuiAccordionSummary-content': {
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              },
            }}
          >
            <Box sx={{ width: 48, height: 48, borderRadius: '12px', backgroundColor: THEME_COLORS.TEXT_PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BusinessIcon sx={{ color: THEME_COLORS.WHITE, fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: THEME_COLORS.TEXT_PRIMARY, fontWeight: 600 }}>
                Plantillas de Notificaciones de Distribuidores
              </Typography>
              <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, mt: 0.5 }}>
                Configura los mensajes automáticos para eventos de distribuidores
              </Typography>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0, backgroundColor: THEME_COLORS.WHITE }}>
            {/* Información sobre comportamiento */}
            <Box sx={{ p: 3, pb: 0 }}>
              <TemplateInfo />
            </Box>
            
            {/* Sistema de Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                sx={{ 
                  px: 3,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem'
                  }
                }}
              >
                <Tab 
                  icon={<Code sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} 
                  label="Editar Plantillas" 
                  sx={{ 
                    minHeight: 48,
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY }
                  }}
                />
                <Tab 
                  icon={<Visibility sx={{ color: THEME_COLORS.TEXT_SECONDARY }} />} 
                  label="Variables Disponibles" 
                  sx={{ 
                    minHeight: 48,
                    color: THEME_COLORS.TEXT_SECONDARY,
                    '&.Mui-selected': { color: THEME_COLORS.TEXT_PRIMARY }
                  }}
                />
              </Tabs>
            </Box>

            {/* Contenido de las Tabs */}
            {activeTab === 0 && (
              <Box sx={{ p: 3 }}>
                {renderTemplateCards()}
                
                <Divider sx={{ my: 4 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={operationStatus === OPERATION_STATUS.LOADING}
                    size="large"
                    sx={{
                      backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                      color: THEME_COLORS.BACKGROUND_PAPER,
                      borderRadius: '12px',
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 160,
                      '&:hover': { 
                        backgroundColor: '#1a1a1a',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {operationStatus === OPERATION_STATUS.LOADING ? 'Guardando...' : 'Guardar Plantillas'}
                  </Button>
                </Box>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ p: 3 }}>
                <DistributorTemplateVariablesPicker 
                  onVariableSelect={handleVariableSelect}
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Modal de Operación */}
      <ConfirmationModal
        open={showOperationModal}
        status={operationStatus}
        title={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Guardando Plantillas'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? '¡Plantillas Guardadas!'
              : 'Error al Guardar'
        }
        subtitle={
          operationStatus === OPERATION_STATUS.LOADING 
            ? 'Por favor espera mientras guardamos las plantillas...'
            : operationStatus === OPERATION_STATUS.SUCCESS 
              ? `Las ${operatingTemplateName} se han actualizado correctamente.`
              : 'Hubo un problema al guardar los cambios.'
        }
        userName={operatingTemplateName}
        hideActions={true}
      />
    </>
  );
};

export default DistributorTemplates;